import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Button } from '@atoms/Button';
import { Input } from '@atoms/Input';
import { catalogService } from '@services/catalogService';
import { normalizeScannedCode, playScanBeep } from '@utils/scan.util';
import type { Product } from '@/types';

type ScanStatus =
  | { kind: 'idle' }
  | { kind: 'lookingUp' }
  | { kind: 'found'; product: Product }
  | { kind: 'new'; barcode: string }
  | { kind: 'error'; message: string };

interface ProductBarcodeCaptureProps {
  barcode: string;
  onBarcodeChange: (barcode: string) => void;
  onProductFound: (product: Product) => void;
  disabled?: boolean;
}

export function ProductBarcodeCapture({
  barcode,
  onBarcodeChange,
  onProductFound,
  disabled = false,
}: ProductBarcodeCaptureProps) {
  const regionId = useId().replace(/:/g, '');
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastScanRef = useRef('');
  const usbBufferRef = useRef('');
  const usbLastKeyRef = useRef(0);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [status, setStatus] = useState<ScanStatus>({ kind: 'idle' });
  const [fileBusy, setFileBusy] = useState(false);

  const stopScanner = useCallback(async () => {
    if (scannerRef.current?.isScanning) {
      try {
        await scannerRef.current.stop();
      } catch {
        /* ignore */
      }
    }
    scannerRef.current = null;
    setCameraActive(false);
  }, []);

  const resolveBarcode = useCallback(
    async (raw: string) => {
      const code = normalizeScannedCode(raw);
      if (!code || code === lastScanRef.current) return;
      lastScanRef.current = code;

      playScanBeep();
      onBarcodeChange(code);
      setStatus({ kind: 'lookingUp' });

      try {
        const product = await catalogService.scanBarcode(code);
        onProductFound(product);
        setStatus({ kind: 'found', product });
      } catch {
        setStatus({ kind: 'new', barcode: code });
      }

      setTimeout(() => {
        lastScanRef.current = '';
      }, 1500);
    },
    [onBarcodeChange, onProductFound],
  );

  useEffect(() => {
    if (!cameraActive || disabled) return;

    let cancelled = false;

    const start = async () => {
      // html5-qrcode needs a visible, laid-out container before start().
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
      });
      if (cancelled) return;

      setCameraError(null);

      try {
        const scanner = new Html5Qrcode(regionId);
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 15, qrbox: { width: 280, height: 160 } },
          (decoded) => {
            void stopScanner().then(() => resolveBarcode(decoded));
          },
          () => {},
        );
      } catch {
        if (!cancelled) {
          setCameraError('Kamera açılamadı. Barkodu manuel girebilir veya fotoğraftan okuyabilirsiniz.');
          setCameraActive(false);
        }
      }
    };

    void start();

    return () => {
      cancelled = true;
      void stopScanner();
    };
  }, [cameraActive, disabled, regionId, resolveBarcode, stopScanner]);

  const scanFromFile = useCallback(
    async (file: File) => {
      if (disabled) return;
      setFileBusy(true);
      setCameraError(null);

      try {
        const scanner = scannerRef.current ?? new Html5Qrcode(regionId);
        if (!scannerRef.current) scannerRef.current = scanner;

        const decoded = await scanner.scanFile(file, true);
        await stopScanner();
        await resolveBarcode(decoded);
      } catch {
        setStatus({ kind: 'error', message: 'Fotoğrafta barkod veya QR kod bulunamadı.' });
      } finally {
        setFileBusy(false);
      }
    },
    [disabled, regionId, resolveBarcode, stopScanner],
  );

  useEffect(() => {
    if (disabled) void stopScanner();
  }, [disabled, stopScanner]);

  const handleUsbKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const now = Date.now();
    if (now - usbLastKeyRef.current > 120) usbBufferRef.current = '';
    usbLastKeyRef.current = now;

    if (event.key === 'Enter') {
      const buffered = usbBufferRef.current.trim();
      usbBufferRef.current = '';
      if (buffered.length >= 3) {
        event.preventDefault();
        void resolveBarcode(buffered);
      } else if (barcode.trim()) {
        void resolveBarcode(barcode);
      }
      return;
    }

    if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      usbBufferRef.current += event.key;
    }
  };

  return (
    <div className="rounded-xl border border-dashed border-brand-300 bg-brand-50/40 p-4 space-y-3">
      <div>
        <p className="text-sm font-semibold text-gray-900">Barkod / QR Tarama</p>
        <p className="text-xs text-gray-500 mt-0.5">
          Kamera, fotoğraf veya USB barkod okuyucu ile tarayın; okunan kod ürün alanlarına aktarılır.
        </p>
      </div>

      {cameraActive && (
        <>
          <div
            id={regionId}
            className="overflow-hidden rounded-lg bg-black border-2 border-brand-400 min-h-[220px] w-full [&_video]:!w-full [&_video]:!h-auto [&_video]:!object-cover"
          />
          <Button
            type="button"
            variant="outline"
            className="w-full border-red-300 text-red-600"
            onClick={() => void stopScanner()}
          >
            Tarayıcıyı Kapat
          </Button>
        </>
      )}

      {!cameraActive && (
        <div className="grid gap-2 sm:grid-cols-2">
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            onClick={() => {
              setCameraError(null);
              setCameraActive(true);
            }}
          >
            Canlı Kamera ile Tara
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={disabled || fileBusy}
            loading={fileBusy}
            onClick={() => fileInputRef.current?.click()}
          >
            Fotoğraftan Oku
          </Button>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void scanFromFile(file);
        }}
      />

      <Input
        label="Barkod / QR No"
        value={barcode}
        disabled={disabled}
        onChange={(e) => {
          onBarcodeChange(e.target.value);
          setStatus({ kind: 'idle' });
        }}
        onKeyDown={handleUsbKeyDown}
        placeholder="8690000000000 veya QR içeriği"
      />

      {cameraError && <p className="text-sm text-amber-700">{cameraError}</p>}

      {status.kind === 'lookingUp' && <p className="text-sm text-gray-600">Ürün aranıyor…</p>}
      {status.kind === 'found' && (
        <p className="text-sm text-green-700">
          Ürün bulundu: <strong>{status.product.name}</strong> — bilgiler forma aktarıldı.
        </p>
      )}
      {status.kind === 'new' && (
        <p className="text-sm text-blue-700">
          Barkod okundu: <strong>{status.barcode}</strong> — sistemde kayıtlı değil, yeni ürün olarak ekleyebilirsiniz.
        </p>
      )}
      {status.kind === 'error' && <p className="text-sm text-red-600">{status.message}</p>}
    </div>
  );
}
